import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAffiliateTracking } from '@/hooks/useAffiliateTracking';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Check, Crown, Users, User, CreditCard, 
  Calendar, Sparkles, AlertCircle
} from 'lucide-react';
import Footer from '@/components/layout/Footer';

// Room images from existing retreat page
const ROOM_IMAGES = {
  dome: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/eif7vw9m_geodesic%20dome.jpeg',
  bungalow: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/72j2xc0z_exotic%20bungalow.png',
  villa: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/shh7wly6_Luxury%20Villa.png'
};

// Room data with inventory and pricing
const ROOMS = [
  {
    id: 'geodesic-dome',
    name: 'Sacred Geodesic Domes',
    icon: '🏕️',
    image: ROOM_IMAGES.dome,
    description: 'Nestled in the lush rainforest along our private river, these domes invite you to reconnect with nature.',
    features: ['King Bed', 'Private jungle deck', 'Open-air rainforest shower', 'Full AC'],
    pricePerPerson: 5297,
    occupancy: 'double',
    occupancyLabel: 'Double Occupancy',
    inventory: 12,
    bedType: 'King Beds',
    deposit: 500,
    // Ontraport form configurations with full parameters
    forms: {
      fullPay: {
        uid: 'p2c260712f44',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1085&formWidth=100%&popPosition=mc&instance=n1090030343'
      },
      paymentPlan2: {
        uid: 'p2c260712f48',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1153&formWidth=100%&popPosition=mc&instance=2055004379'
      },
      paymentPlan3: {
        uid: 'p2c260712f47',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1153&formWidth=100%&popPosition=mc&instance=2055004379'
      },
      fullPayVip: {
        uid: 'p2c260712f55',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1148&formWidth=100%&popPosition=mc&instance=1524140609'
      },
      paymentPlan2Vip: {
        uid: 'p2c260712f56',
        params: 'borderColor=#fff&borderSize=5px&embed=true&formHeight=600&formWidth=480px&popPosition=mc&instance=1751639376'
      },
      paymentPlan3Vip: {
        uid: 'p2c260712f57',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1216&formWidth=100%&popPosition=mc&instance=374208035'
      }
    }
  },
  {
    id: 'bungalow-double',
    name: 'Exotic Jungle Bungalows (Double)',
    icon: '🌴',
    image: ROOM_IMAGES.bungalow,
    description: 'Bali-inspired bungalows with spacious bathrooms opening to sweeping jungle views.',
    features: ['Twin Beds', 'Jungle deck', 'Soaking tub with views', 'AC available'],
    pricePerPerson: 4997,
    occupancy: 'double',
    occupancyLabel: 'Double Occupancy',
    inventory: 2,
    bedType: 'Twin Beds',
    deposit: 500,
    forms: {
      fullPay: {
        uid: 'p2c260712f46',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1085&formWidth=100%&popPosition=mc&instance=n1090030343'
      },
      paymentPlan2: {
        uid: 'p2c260712f50',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1153&formWidth=100%&popPosition=mc&instance=2055004379'
      },
      paymentPlan3: {
        uid: 'p2c260712f49',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1153&formWidth=100%&popPosition=mc&instance=2055004379'
      },
      fullPayVip: {
        uid: 'p2c260712f60',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1148&formWidth=100%&popPosition=mc&instance=1524140609'
      },
      paymentPlan2Vip: {
        uid: 'p2c260712f59',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1216&formWidth=100%&popPosition=mc&instance=374208035'
      },
      paymentPlan3Vip: {
        uid: 'p2c260712f58',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1216&formWidth=100%&popPosition=mc&instance=374208035'
      }
    }
  },
  {
    id: 'bungalow-single',
    name: 'Exotic Jungle Bungalows (Single)',
    icon: '🌴',
    image: ROOM_IMAGES.bungalow,
    description: 'Bali-inspired bungalows with spacious bathrooms opening to sweeping jungle views. Enjoy private sanctuary.',
    features: ['King Bed', 'Jungle deck', 'Soaking tub with views', 'AC available', 'Private room'],
    pricePerPerson: 5997,
    occupancy: 'single',
    occupancyLabel: 'Single Occupancy',
    inventory: 3,
    bedType: 'King Bed',
    deposit: 500,
    forms: {
      fullPay: {
        uid: 'p2c260712f45',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1142&formWidth=100%&popPosition=mc&instance=n580659013'
      },
      paymentPlan2: {
        uid: 'p2c260712f52',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1153&formWidth=100%&popPosition=mc&instance=2055004379'
      },
      paymentPlan3: {
        uid: 'p2c260712f51',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1153&formWidth=100%&popPosition=mc&instance=2055004379'
      },
      fullPayVip: {
        uid: 'p2c260712f61',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1205&formWidth=100%&popPosition=mc&instance=2033511939'
      },
      paymentPlan2Vip: {
        uid: 'p2c260712f62',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1216&formWidth=100%&popPosition=mc&instance=374208035'
      },
      paymentPlan3Vip: {
        uid: 'p2c260712f63',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1216&formWidth=100%&popPosition=mc&instance=374208035'
      }
    }
  },
  {
    id: 'luxury-villa',
    name: 'Luxurious Private Villas',
    icon: '🏡',
    image: ROOM_IMAGES.villa,
    description: 'Hand-carved Balinese treasures - 170-180 year old villas with private plunge pools.',
    features: ['King Bed', 'Private plunge pool', 'Ocean & rainforest views', 'Open-air design'],
    pricePerPerson: 6597,
    occupancy: 'single',
    occupancyLabel: 'Single Occupancy',
    inventory: 2,
    bedType: 'King Bed',
    deposit: 1000,
    villaOptions: ['The Jogolo House', 'The Potoo House'],
    forms: {
      fullPay: {
        uid: 'p2c260712f43',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1085&formWidth=100%&popPosition=mc&instance=n1090030343'
      },
      paymentPlan2: {
        uid: 'p2c260712f53',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1153&formWidth=100%&popPosition=mc&instance=2055004379'
      },
      paymentPlan3: {
        uid: 'p2c260712f54',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1153&formWidth=100%&popPosition=mc&instance=2055004379'
      },
      fullPayVip: {
        uid: 'p2c260712f64',
        params: 'borderColor=#fff&borderSize=5px&embed=true&formHeight=600&formWidth=480px&popPosition=mc&instance=1751639376'
      },
      paymentPlan2Vip: {
        uid: 'p2c260712f65',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1216&formWidth=100%&popPosition=mc&instance=374208035'
      },
      paymentPlan3Vip: {
        uid: 'p2c260712f66',
        params: 'borderColor=#000000&borderSize=0px&embed=true&formHeight=1216&formWidth=100%&popPosition=mc&instance=374208035'
      }
    }
  }
];

// Determine available payment option based on current date
// Before March 1, 2026: 3-Pay available
// March 1 - April 1: 2-Pay available  
// April 1 - May 1: 2-Pay (final payment before departure)
// After May 1: Full pay only
const getAvailablePaymentOption = () => {
  const now = new Date();
  const march1 = new Date(2026, 2, 1);  // March 1, 2026
  const april1 = new Date(2026, 3, 1);  // April 1, 2026
  const may1 = new Date(2026, 4, 1);    // May 1, 2026
  const june1 = new Date(2026, 5, 1);   // June 1, 2026

  if (now < march1) {
    return { 
      type: '3pay', 
      installments: 3, 
      label: '3-Pay Plan', 
      description: 'Split into 3 payments',
      dueText: 'Due in March, April, and May',
      formKeySuffix: 'paymentPlan3'
    };
  } else if (now < april1) {
    return { 
      type: '2pay', 
      installments: 2, 
      label: '2-Pay Plan', 
      description: 'Split into 2 payments',
      dueText: 'Due in April and May',
      formKeySuffix: 'paymentPlan2'
    };
  } else if (now < may1) {
    return { 
      type: '2pay', 
      installments: 1, 
      label: '2-Pay Plan', 
      description: 'Split into 2 equal payments',
      dueText: 'Due in May',
      formKeySuffix: 'paymentPlan2'
    };
  } else if (now < june1) {
    return { 
      type: '2pay', 
      installments: 1, 
      label: '2-Pay Plan', 
      description: 'Split into 2 equal payments',
      dueText: 'Due in June',
      formKeySuffix: 'paymentPlan2'
    };
  } else {
    return { 
      type: 'full', 
      installments: 1, 
      label: null, 
      description: null,
      dueText: null,
      formKeySuffix: null
    };
  }
};

const VIP_UPGRADE = {
  price: 1500,
  features: [
    'Priority access to all experiences',
    'Exclusive VIP-only gatherings',
    'Premium amenity package',
    'Personal concierge service'
  ],
  form: 'ONTRAPORT_VIP_ONLY_URL'
};

const RetreatBookingPage = () => {
  const navigate = useNavigate();
  useAffiliateTracking();
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [paymentOption, setPaymentOption] = useState('full'); // 'full' or 'plan' (determined by date)
  const [addVip, setAddVip] = useState(false);
  const [selectedVilla, setSelectedVilla] = useState('');
  const [inventory, setInventory] = useState({});
  const [loadingInventory, setLoadingInventory] = useState(true);
  
  // Fetch live inventory from backend
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/retreat/inventory`);
        const data = await response.json();
        setInventory(data.inventory || {});
      } catch (error) {
        console.error('Error fetching inventory:', error);
        // Fall back to static inventory
        setInventory({
          'geodesic-dome': 12,
          'bungalow-double': 2,
          'bungalow-single': 5,
          'luxury-villa': 2
        });
      } finally {
        setLoadingInventory(false);
      }
    };
    
    // Initial fetch
    fetchInventory();
    
    // Auto-refresh every 30 seconds
    const intervalId = setInterval(fetchInventory, 30000);
    
    // Cleanup on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Merge static room data with live inventory
  const roomsWithInventory = ROOMS.map(room => ({
    ...room,
    inventory: inventory[room.id] !== undefined ? inventory[room.id] : room.inventory
  }));
  
  // Get available payment option based on current date
  const availablePayment = getAvailablePaymentOption();
  const hasPaymentPlan = availablePayment.type !== 'full';

  // Admin fee for payment plans (added to total when using installments)
  const PAYMENT_PLAN_ADMIN_FEE = 500;

  // Calculate payment plan details with admin fee
  const getPaymentPlanDetails = () => {
    if (!selectedRoom || !hasPaymentPlan) return null;
    const basePrice = selectedRoom.pricePerPerson + (addVip ? VIP_UPGRADE.price : 0);
    // Add admin fee for payment plans
    const totalWithFee = basePrice + PAYMENT_PLAN_ADMIN_FEE;
    // 50/50 split: half down today, half due later
    const downPayment = totalWithFee / 2;
    const perPayment = totalWithFee / 2;
    return {
      installments: availablePayment.installments,
      perPayment: perPayment.toFixed(2),
      deposit: downPayment,
      totalWithFee: totalWithFee,
      adminFee: PAYMENT_PLAN_ADMIN_FEE,
      dueText: availablePayment.dueText
    };
  };

  // Get total price
  const getTotalPrice = () => {
    if (!selectedRoom) return 0;
    let total = selectedRoom.pricePerPerson;
    if (addVip) total += VIP_UPGRADE.price;
    return total;
  };

  // Ontraport hosted page URLs for each room/payment combination
  const ONTRAPORT_URLS = {
    // Geodesic Domes
    'geodesic-dome-fullPay': 'https://support.sisterdiamonds.com/GeodesicDomesFullPay',
    'geodesic-dome-paymentPlan2': 'https://support.sisterdiamonds.com/GeodesicDomes2Pay',
    'geodesic-dome-paymentPlan3': 'https://support.sisterdiamonds.com/GeodesicDomes3Pay',
    'geodesic-dome-fullPayVip': 'https://support.sisterdiamonds.com/GeodesicDomesFullPayVIP',
    'geodesic-dome-paymentPlan2Vip': 'https://support.sisterdiamonds.com/GeodesicDomes2PayVIP',
    'geodesic-dome-paymentPlan3Vip': 'https://support.sisterdiamonds.com/GeodesicDomes3Payvip',
    
    // Bungalow Single
    'bungalow-single-fullPay': 'https://support.sisterdiamonds.com/BungalowSingleFullPay',
    'bungalow-single-paymentPlan2': 'https://support.sisterdiamonds.com/BunglowSingle2Pay',
    'bungalow-single-paymentPlan3': 'https://support.sisterdiamonds.com/BunglowSingle3Pay',
    'bungalow-single-fullPayVip': 'https://support.sisterdiamonds.com/BungalowSingleFullPayVip',
    'bungalow-single-paymentPlan2Vip': 'https://support.sisterdiamonds.com/BunglowSingle2PayVip',
    'bungalow-single-paymentPlan3Vip': 'https://support.sisterdiamonds.com/BunglowSingle3PayVip',
    
    // Bungalow Double
    'bungalow-double-fullPay': 'https://support.sisterdiamonds.com/BunglowDoubleFullpay',
    'bungalow-double-paymentPlan2': 'https://support.sisterdiamonds.com/BunglowDouble2pay',
    'bungalow-double-paymentPlan3': 'https://support.sisterdiamonds.com/BunglowDouble3pay',
    'bungalow-double-fullPayVip': 'https://support.sisterdiamonds.com/BunglowDoubleFullVip',
    'bungalow-double-paymentPlan2Vip': 'https://support.sisterdiamonds.com/BunglowDouble2PayVip',
    'bungalow-double-paymentPlan3Vip': 'https://support.sisterdiamonds.com/BunglowDouble3payVip',
    
    // Luxury Villa
    'luxury-villa-fullPay': 'https://support.sisterdiamonds.com/LuxuryVillaFullPay',
    'luxury-villa-paymentPlan2': 'https://support.sisterdiamonds.com/LuxuryVilla2Pay',
    'luxury-villa-paymentPlan3': 'https://support.sisterdiamonds.com/LuxuryVilla3Pay',
    'luxury-villa-fullPayVip': 'https://support.sisterdiamonds.com/LuxuryVillaFullPayVIP',
    'luxury-villa-paymentPlan2Vip': 'https://support.sisterdiamonds.com/LuxuryVilla2Payvip',
    'luxury-villa-paymentPlan3Vip': 'https://support.sisterdiamonds.com/LuxuryVilla3Payvip',
  };

  const handleCheckout = () => {
    if (!selectedRoom) {
      alert('Please select a room.');
      return;
    }
    
    // Build the form key
    let formKey;
    if (paymentOption === 'full') {
      formKey = addVip ? 'fullPayVip' : 'fullPay';
    } else {
      formKey = addVip ? `${availablePayment.formKeySuffix}Vip` : availablePayment.formKeySuffix;
    }
    
    const urlKey = `${selectedRoom.id}-${formKey}`;
    const ontraportUrl = ONTRAPORT_URLS[urlKey];
    
    if (ontraportUrl) {
      // Redirect to Ontraport hosted page
      window.location.href = ontraportUrl;
    } else {
      // Fallback to checkout page with iframe (for any missing URLs)
      const total = selectedRoom.pricePerPerson + (addVip ? VIP_UPGRADE.price : 0);
      navigate(`/retreat-checkout?room=${selectedRoom.id}&plan=${formKey}&total=${total}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand/30 to-white">
      {/* Header */}
      <div className="bg-forest text-white py-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/retreats" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" />
            Back to Retreat Details
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <Badge className="bg-terracotta/10 text-terracotta mb-4">June 26 - July 1, 2026</Badge>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-forest mb-3">
            Book Your Retreat Experience
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Just Be: Soft Power™ Retreat at Imiloa Institute, Costa Rica
          </p>
        </div>

        {/* Step 1: Select Room */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
            <h2 className="font-heading text-xl md:text-2xl text-forest">Select Your Accommodation</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {roomsWithInventory.map((room) => (
              <Card 
                key={room.id}
                className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                  selectedRoom?.id === room.id 
                    ? 'ring-2 ring-forest shadow-xl scale-[1.02]' 
                    : 'hover:shadow-lg hover:scale-[1.01]'
                } ${room.inventory === 0 ? 'opacity-60' : ''}`}
                onClick={() => room.inventory > 0 && setSelectedRoom(room)}
                data-testid={`room-${room.id}`}
              >
                {/* Room Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={room.image} 
                    alt={room.name}
                    className="w-full h-full object-cover"
                  />
                  {/* Inventory Badge */}
                  <div className="absolute top-3 right-3">
                    {room.inventory > 0 ? (
                      <Badge className={`${room.inventory <= 3 ? 'bg-red-500' : 'bg-forest'} text-white`}>
                        {room.inventory} {room.inventory === 1 ? 'spot' : 'spots'} left
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-500 text-white">Sold Out</Badge>
                    )}
                  </div>
                  {/* Selected Check */}
                  {selectedRoom?.id === room.id && (
                    <div className="absolute top-3 left-3 h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center">
                      <Check className="h-5 w-5" />
                    </div>
                  )}
                </div>

                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-heading text-lg text-forest">{room.icon} {room.name}</h3>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{room.description}</p>
                  
                  {/* Occupancy */}
                  <div className="flex items-center gap-2 mb-3">
                    {room.occupancy === 'double' ? (
                      <Users className="h-4 w-4 text-forest" />
                    ) : (
                      <User className="h-4 w-4 text-forest" />
                    )}
                    <span className="text-sm text-gray-600">{room.occupancyLabel}</span>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {room.features.slice(0, 3).map((feature, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{feature}</Badge>
                    ))}
                  </div>

                  {/* Price */}
                  <div className="border-t pt-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-forest">${room.pricePerPerson.toLocaleString()}</span>
                      <span className="text-gray-500 text-sm">per person</span>
                    </div>
                    {room.note && (
                      <p className="text-xs text-terracotta mt-1">{room.note}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Step 2: Payment Option (only show if room selected) */}
        {selectedRoom && (
          <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
              <h2 className="font-heading text-xl md:text-2xl text-forest">Choose Payment Option</h2>
            </div>

            {/* Show available payment options based on date */}
            <div className={`grid ${hasPaymentPlan ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md'} gap-4`}>
              {/* Full Pay - Always Available */}
              <Card 
                className={`cursor-pointer transition-all ${
                  paymentOption === 'full' ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'
                }`}
                onClick={() => setPaymentOption('full')}
                data-testid="payment-full"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-6 w-6 text-forest" />
                      <div>
                        <h3 className="font-heading text-lg text-forest">Pay in Full</h3>
                        <p className="text-sm text-gray-500">One-time payment</p>
                      </div>
                    </div>
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                      paymentOption === 'full' ? 'border-forest bg-forest' : 'border-gray-300'
                    }`}>
                      {paymentOption === 'full' && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-forest">
                    ${(selectedRoom.pricePerPerson + (addVip ? VIP_UPGRADE.price : 0)).toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              {/* Payment Plan - Only if available based on date */}
              {hasPaymentPlan && (
                <Card 
                  className={`cursor-pointer transition-all ${
                    paymentOption === 'plan' ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'
                  }`}
                  onClick={() => setPaymentOption('plan')}
                  data-testid="payment-plan"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-6 w-6 text-forest" />
                        <div>
                          <h3 className="font-heading text-lg text-forest">{availablePayment.label}</h3>
                          <p className="text-sm text-gray-500">{availablePayment.description}</p>
                        </div>
                      </div>
                      <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                        paymentOption === 'plan' ? 'border-forest bg-forest' : 'border-gray-300'
                      }`}>
                        {paymentOption === 'plan' && <Check className="h-4 w-4 text-white" />}
                      </div>
                    </div>
                    {(() => {
                      const planDetails = getPaymentPlanDetails();
                      return planDetails ? (
                        <div>
                          <div className="text-2xl font-bold text-forest mb-1">
                            ${planDetails.deposit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} <span className="text-base font-normal text-gray-500">down today</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            + 1 payment of ${parseFloat(planDetails.perPayment).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">{planDetails.dueText}</p>
                          <p className="text-xs text-gray-400">Includes ${planDetails.adminFee} admin fee</p>
                        </div>
                      ) : null;
                    })()}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Notice when payment plan not available */}
            {!hasPaymentPlan && (
              <p className="text-sm text-amber-600 mt-4 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Payment plans are no longer available. Full payment is required for bookings after June 1st.
              </p>
            )}
          </div>
        )}

        {/* Step 3: VIP Upgrade (only show if room selected) */}
        {selectedRoom && (
          <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '150ms' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
              <h2 className="font-heading text-xl md:text-2xl text-forest">VIP Upgrade (Optional)</h2>
            </div>

            <Card 
              className={`cursor-pointer transition-all ${
                addVip ? 'ring-2 ring-terracotta shadow-lg bg-terracotta/5' : 'hover:shadow-md'
              }`}
              onClick={() => setAddVip(!addVip)}
              data-testid="vip-upgrade"
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                      addVip ? 'bg-terracotta text-white' : 'bg-terracotta/10 text-terracotta'
                    }`}>
                      <Crown className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-forest mb-1">VIP Experience Upgrade</h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {VIP_UPGRADE.features.map((feature, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-white">{feature}</Badge>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Full payment only - no payment plan available for VIP
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-terracotta">+${VIP_UPGRADE.price.toLocaleString()}</div>
                    </div>
                    <div className={`h-6 w-6 rounded border-2 flex items-center justify-center ${
                      addVip ? 'border-terracotta bg-terracotta' : 'border-gray-300'
                    }`}>
                      {addVip && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Villa Selection (if luxury villa selected) */}
        {selectedRoom?.id === 'luxury-villa' && (
          <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">
                <Sparkles className="h-4 w-4" />
              </div>
              <h2 className="font-heading text-xl md:text-2xl text-forest">Select Your Villa</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {selectedRoom.villaOptions.map((villa) => (
                <Card 
                  key={villa}
                  className={`cursor-pointer transition-all ${
                    selectedVilla === villa ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'
                  }`}
                  onClick={() => setSelectedVilla(villa)}
                  data-testid={`villa-${villa.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <CardContent className="p-5 flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-lg text-forest">🌿 {villa}</h3>
                      <p className="text-sm text-gray-500">1 King Bed • Private Plunge Pool</p>
                    </div>
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                      selectedVilla === villa ? 'border-forest bg-forest' : 'border-gray-300'
                    }`}>
                      {selectedVilla === villa && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Order Summary & Checkout */}
        {selectedRoom && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '300ms' }}>
            <Card className="bg-forest text-white overflow-hidden">
              <CardContent className="p-6 md:p-8">
                <h3 className="font-heading text-xl mb-6">Order Summary</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-white/80">{selectedRoom.name}</span>
                    <span>${selectedRoom.pricePerPerson.toLocaleString()}</span>
                  </div>
                  {addVip && (
                    <div className="flex justify-between">
                      <span className="text-white/80">VIP Upgrade</span>
                      <span>+${VIP_UPGRADE.price.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-white/20 pt-3 flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span>${getTotalPrice().toLocaleString()}</span>
                  </div>
                  
                  {paymentOption === 'plan' && getPaymentPlanDetails() && (
                    <div className="bg-white/10 rounded-lg p-4 mt-4">
                      <p className="text-sm text-white/80 mb-2">Payment Schedule:</p>
                      <p className="font-medium">${getPaymentPlanDetails().deposit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} down today</p>
                      <p className="text-sm text-white/70">+ 1 payment of ${parseFloat(getPaymentPlanDetails().perPayment).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                      <p className="text-xs text-white/50 mt-1">{getPaymentPlanDetails().dueText}</p>
                      <p className="text-xs text-white/50">Includes ${getPaymentPlanDetails().adminFee} admin fee</p>
                    </div>
                  )}
                  
                  {addVip && paymentOption === 'plan' && (
                    <div className="bg-terracotta/20 rounded-lg p-4 mt-4 flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <p className="text-sm">VIP upgrade requires full payment. You'll be charged the full amount today.</p>
                    </div>
                  )}
                </div>

                <Button 
                  size="lg" 
                  className="w-full bg-terracotta hover:bg-terracotta/90 text-white h-14 text-lg"
                  onClick={handleCheckout}
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                </Button>

                <p className="text-center text-white/60 text-sm mt-4">
                  You'll be redirected to our secure payment portal
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* No Selection Prompt */}
        {!selectedRoom && (
          <div className="text-center py-12 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>Select an accommodation above to continue</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default RetreatBookingPage;
